// @ts-check
import {
  Badge,
  Box,
  ChevronDownIcon,
  Link,
  SkeletonBox,
  SkeletonText,
  TableCell,
  TableRow,
  Text,
} from '~/design-system';
import { Trans } from '@lingui/macro';
import React from 'react';
import BrandLogo from '../../BrandLogo';

/**
 * Renders the partners rows
 * @param {Object} props
 * @param {Object[]} props.partners - the list of partners to be rendered
 * @param {Object} props.suppliersMap - the mapping between suppliers and partner ids
 * @param {String} props.selectedSupplier - the name of the selected supplier
 * @param {function} props.onSelectPartner - callback when a partner is matched
 * @param {String | undefined} props.companyId - the id of the selected company
 * @param {String | undefined} props.partnerId - the id of the selected partner
 */
export default function Partners({
  partners,
  suppliersMap,
  selectedSupplier,
  onSelectPartner,
  companyId,
  partnerId,
  ...rest
}) {
  return (
    <React.Fragment>
      {partners?.map((partner) => {
        const matched = suppliersMap?.[selectedSupplier] === partner.partnerId;
        const suggested = partner && partner.name === selectedSupplier;
        let isSelected = false;
        if (!companyId && !partnerId) {
          isSelected = matched;
        } else {
          isSelected = partnerId === partner.partnerId;
        }

        return (
          <PartnerTableRow
            key={partner.partnerId}
            isSelected={isSelected}
            onClick={() => {
              onSelectPartner(partner);
            }}
            {...rest}
          >
            <RowPartnerSelection checked={isSelected} sx={{ width: 50 }} />
            <RowLogo partner={partner} sx={{ width: 100 }} />
            <RowName partner={partner} />
            <RowBadges
              matched={matched}
              suggested={suggested}
              sx={{ width: 50 }}
            />
          </PartnerTableRow>
        );
      })}
    </React.Fragment>
  );
}

function RowPartnerSelection({ checked, ...rest }) {
  return (
    <TableCell sx={{ height: '100px' }} {...rest}>
      <input type="checkbox" checked={checked} readOnly />
    </TableCell>
  );
}

function PartnerTableRow({ isSelected, onClick, children, ...rest }) {
  return (
    <TableRow
      sx={{
        cursor: 'pointer',
        ':hover': {
          color: 'primary',
          backgroundColor: isSelected ? 'selected' : 'light',
        },
        backgroundColor: isSelected ? 'selected' : '',
      }}
      onClick={onClick}
      {...rest}
    >
      {children}
    </TableRow>
  );
}

function RowBadges({ matched, suggested, ...rest }) {
  return (
    <TableCell {...rest}>
      {matched && (
        <Badge variant="success">
          <Trans>Matched</Trans>
        </Badge>
      )}
      {!matched && suggested && (
        <Badge variant="default">
          <Trans>Suggested</Trans>
        </Badge>
      )}
    </TableCell>
  );
}

function RowLogo({ partner, ...rest }) {
  return (
    <TableCell sx={{ height: '100px' }} {...rest}>
      <BrandLogo brand={partner.brand} />
    </TableCell>
  );
}

function RowName({ partner, ...rest }) {
  const [showMore, setShowMore] = React.useState(false);
  const alias = partner?.aliases?.join(', ');
  const street = partner?.brand?.street;
  const country = partner?.brand?.country;
  const city = partner?.brand?.city;
  const postalCode = partner?.brand?.postalCode;
  const postalCodeAndCity = [postalCode, city].filter(Boolean).join(' ');
  const address = [country, postalCodeAndCity, street]
    .filter(Boolean)
    .join(', ');

  return (
    <TableCell sx={{ textAlign: 'left' }} {...rest}>
      <Text variant="default">{partner?.brand?.name}</Text>

      {address && <Text variant="smallest">{address}</Text>}
      {alias && (
        <Text
          variant="smallest"
          sx={{
            color: 'brand.lightGraphite',
          }}
        >
          <Trans>Alias:</Trans> {alias}
        </Text>
      )}
      <Box>
        <Link
          onClick={() => {
            setShowMore(!showMore);
            console.log('todo: show more...');
          }}
        >
          {showMore ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}
          <ChevronDownIcon />
        </Link>
        {showMore && (
          <Text variant="smallest" sx={{}}>
            Company ID: {partner.companyId}
          </Text>
        )}
      </Box>
    </TableCell>
  );
}

export function BrandSkeletonRow() {
  return (
    <TableRow>
      <TableCell sx={{ height: 100, width: 50 }}>
        <input type="checkbox" />
      </TableCell>
      <TableCell sx={{ height: 100, with: 100 }}>
        <SkeletonBox sx={{ height: 50 }} />
      </TableCell>
      <TableCell sx={{ height: 100 }}>
        <SkeletonText />
        <SkeletonText />
        <SkeletonText />
      </TableCell>
      <TableCell sx={{ height: 100, width: 50 }}></TableCell>
    </TableRow>
  );
}
